import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Users, Search, Archive, RotateCcw, Radio } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { workgroupAvatarSrc, WORKGROUP_UPDATED_EVENT, type WorkgroupUpdatedDetail } from '@/lib/workgroup-avatar';
import { EmptyState } from '@/components/aurora/EmptyState';
import { AuroraGlassPanel } from '@easyflow/ui-frontend';
import { auroraPalette } from '@/components/aurora/tokens';
import { useIsMobile } from '@/hooks/use-media-query';
import MobileWorkgroups from '@/pages/mobile/MobileWorkgroups';
import { messengerApi } from './lib/messengerApi';
import CreateWorkgroupModal from './workgroup-hub/CreateWorkgroupModal';
import { WorkgroupBadge } from './workgroup-hub/BadgePicker';
import {
  CATEGORY_LABELS,
  type Workgroup,
  type WorkgroupCategory,
} from './workgroup-hub/types';

type ActiveHuddleMap = Record<string, { recording: boolean }>;

interface ActiveHuddleProbe {
  id: string;
  recording_requested?: boolean | null;
}

const THEME_ACCENT: Record<string, { border: string; dot: string }> = {
  blue:    { border: 'border-l-blue-500',    dot: 'bg-blue-500' },
  emerald: { border: 'border-l-emerald-500', dot: 'bg-emerald-500' },
  purple:  { border: 'border-l-purple-500',  dot: 'bg-purple-500' },
  orange:  { border: 'border-l-orange-500',  dot: 'bg-orange-500' },
  pink:    { border: 'border-l-pink-500',    dot: 'bg-pink-500' },
  cyan:    { border: 'border-l-cyan-500',    dot: 'bg-cyan-500' },
  amber:   { border: 'border-l-amber-500',   dot: 'bg-amber-500' },
  indigo:  { border: 'border-l-indigo-500',  dot: 'bg-indigo-500' },
  red:     { border: 'border-l-red-500',     dot: 'bg-red-500' },
  teal:    { border: 'border-l-teal-500',    dot: 'bg-teal-500' },
};

function getAccent(theme?: string | null) {
  if (theme && THEME_ACCENT[theme]) return THEME_ACCENT[theme]!;
  return { border: 'border-l-indigo-500', dot: 'bg-indigo-500' };
}

function formatRelative(iso?: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'ora';
  if (min < 60) return `${min}m fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h fa`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}g fa`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mes fa`;
  return `${Math.floor(mo / 12)}a fa`;
}

export default function WorkgroupsListPage() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileWorkgroups /> : <WorkgroupsListPageDesktop />;
}

function WorkgroupsListPageDesktop() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [workgroups, setWorkgroups] = useState<Workgroup[]>([]);
  const [archived, setArchived] = useState<Workgroup[]>([]);
  const [activeHuddles, setActiveHuddles] = useState<ActiveHuddleMap>({});
  const [unreadByConv, setUnreadByConv] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<WorkgroupCategory | ''>('');
  const [showArchived, setShowArchived] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<Workgroup[]>('/messenger/workgroups');
      setWorkgroups(res.data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadArchived = useCallback(async () => {
    try {
      const res = await apiClient.get<Workgroup[]>('/messenger/workgroups/archived');
      setArchived(res.data ?? []);
    } catch {
      setArchived([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showArchived) loadArchived(); }, [showArchived, loadArchived]);

  // Unread per conversation_id (parità mobile, polling 30s).
  useEffect(() => {
    if (showArchived) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await messengerApi.listConversations({});
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const c of res.data ?? []) {
          if (c.unread_count > 0) map[c.id] = c.unread_count;
        }
        setUnreadByConv(map);
      } catch {
        /* noop */
      }
    };
    fetchUnread();
    const t = window.setInterval(fetchUnread, 30000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [showArchived]);

  // Probe huddle attive per i workgroup correnti (parità mobile, polling 20s).
  useEffect(() => {
    if (showArchived || workgroups.length === 0) return;
    let cancelled = false;
    const probe = async () => {
      const entries = await Promise.all(workgroups.map(async (w) => {
        try {
          const res = await apiClient.get<ActiveHuddleProbe | null>(
            `/messenger/workgroups/${w.id}/huddle/active`,
          );
          if (res.data) return [w.id, { recording: !!res.data.recording_requested }] as const;
          return null;
        } catch {
          return null;
        }
      }));
      if (cancelled) return;
      const map: ActiveHuddleMap = {};
      for (const e of entries) if (e) map[e[0]] = e[1];
      setActiveHuddles(map);
    };
    probe();
    const id = window.setInterval(probe, 20000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [workgroups, showArchived]);

  // Task #1849 — quando un workgroup viene aggiornato (avatar, nome, tema…)
  // da un'altra superficie (Hub header, sheet mobile), aggiorniamo in place
  // la riga corrispondente senza dover rifare la fetch della lista.
  useEffect(() => {
    const onUpdated = (evt: Event) => {
      const detail = (evt as CustomEvent<WorkgroupUpdatedDetail>).detail;
      const incoming = detail?.workgroup;
      if (!incoming) return;
      const patch = (list: Workgroup[]) =>
        list.map(w => (w.id === incoming.id ? { ...w, ...incoming } : w));
      setWorkgroups(prev => patch(prev));
      setArchived(prev => patch(prev));
    };
    window.addEventListener(WORKGROUP_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(WORKGROUP_UPDATED_EVENT, onUpdated);
  }, []);

  const tenantSlug = window.location.pathname.split('/')[1] || '';
  const list = showArchived ? archived : workgroups;
  const filtered = list.filter(w => {
    if (categoryFilter && w.category !== categoryFilter) return false;
    if (query && !w.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  async function restoreWg(id: string) {
    setRestoringId(id);
    try {
      await apiClient.patch(`/messenger/workgroups/${id}/restore`, {});
      toast({ title: 'Gruppo ripristinato', description: 'È di nuovo disponibile nella lista attiva.' });
      await Promise.all([loadArchived(), load()]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore di rete';
      toast({ title: 'Ripristino fallito', description: msg, variant: 'destructive' });
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <div className="flex flex-col h-full">
      <AuroraGlassPanel as="header" intensity="medium" className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-semibold text-foreground">Workgroups</h1>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-primary hover:bg-primary/90 rounded-lg text-primary-foreground text-sm flex items-center gap-2"
          >
            <Plus size={14} /> Nuovo
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca..."
              className="w-full bg-muted border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-foreground"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as WorkgroupCategory | '')}
            className="bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
          >
            <option value="">Tutte le categorie</option>
            {(Object.keys(CATEGORY_LABELS) as WorkgroupCategory[]).map(c => (
              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
            ))}
          </select>
          <button
            onClick={() => setShowArchived(v => !v)}
            className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 ${
              showArchived ? 'bg-amber-600/20 text-amber-300 border border-amber-500/40' : 'bg-muted text-muted-foreground border border-border'
            }`}
          >
            <Archive size={14} /> Archiviati
          </button>
        </div>
      </AuroraGlassPanel>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground"><Loader2 className="animate-spin inline" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState
            illustration="no-workgroups"
            title={showArchived ? 'Nessun workgroup archiviato' : 'Nessun workgroup'}
            subtitle={showArchived
              ? 'I gruppi archiviati appariranno qui. Puoi sempre ripristinarli dall\'archivio.'
              : 'Crea il tuo primo workgroup per coordinare chat, task, eventi e file in un unico spazio.'}
            cta={!showArchived && (
              <button
                onClick={() => setShowCreate(true)}
                className="px-4 py-2 bg-primary hover:bg-primary/90 rounded-lg text-primary-foreground text-sm inline-flex items-center gap-2"
              >
                <Plus size={14} /> Crea il primo workgroup
              </button>
            )}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
            {filtered.map(wg => {
              const accent = getAccent(wg.theme_color);
              const updated = formatRelative(wg.updated_at);
              const initials = wg.name.substring(0, 2).toUpperCase();
              const huddle = activeHuddles[wg.id];
              const unread = wg.conversation_id ? (unreadByConv[wg.conversation_id] ?? 0) : 0;
              return (
                <div
                  key={wg.id}
                  className={`relative bg-card border border-border ${accent.border} border-l-4 rounded-xl px-3 py-2.5 hover:border-primary/40 hover:bg-card/80 transition group`}
                >
                  <button
                    type="button"
                    onClick={() => showArchived ? undefined : navigate(`/${tenantSlug}/messenger/workgroups/${wg.id}`)}
                    disabled={showArchived}
                    className="w-full flex items-center gap-3 text-left disabled:cursor-default"
                    aria-label={`Apri ${wg.name}`}
                  >
                    <div className="w-11 h-11 shrink-0 rounded-xl bg-muted border border-border flex items-center justify-center text-foreground text-sm font-semibold overflow-hidden">
                      {wg.avatar_url ? (
                        <img src={workgroupAvatarSrc(wg)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span aria-hidden>{initials}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-foreground text-sm truncate">{wg.name}</h3>
                        {wg.category && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-border text-muted-foreground shrink-0">
                            <span className={`w-1.5 h-1.5 rounded-full ${accent.dot}`} aria-hidden />
                            {CATEGORY_LABELS[wg.category]}
                          </span>
                        )}
                        {huddle && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-[10px] uppercase tracking-wide text-emerald-300 font-semibold shrink-0"
                            aria-label="Video Call Live attiva"
                          >
                            <Radio className={`w-2.5 h-2.5 ${huddle.recording ? 'animate-pulse text-red-300' : ''}`} />
                            {huddle.recording ? 'REC' : 'LIVE'}
                          </span>
                        )}
                        {showArchived && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 shrink-0">
                            Archiviato
                          </span>
                        )}
                        {unread > 0 && (
                          <span
                            className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[11px] font-semibold text-white shrink-0"
                            style={{ background: auroraPalette.indigo }}
                            aria-label={`${unread} messaggi non letti`}
                            data-testid={`desktop-workgroup-unread-${wg.id}`}
                          >
                            {unread > 99 ? '99+' : unread}
                          </span>
                        )}
                      </div>
                      {wg.description && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{wg.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-[11px] text-muted-foreground/80 mt-1">
                        <span className="inline-flex items-center gap-1"><Users size={11} /> {wg.member_count ?? 0}</span>
                        {updated && <span>· aggiornato {updated}</span>}
                      </div>
                    </div>
                    {wg.badge_icon && (
                      <div className="shrink-0 ml-2">
                        <WorkgroupBadge name={wg.badge_icon} themeColor={wg.theme_color} size={32} />
                      </div>
                    )}
                  </button>
                  {showArchived && (
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => restoreWg(wg.id)}
                        disabled={restoringId === wg.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border bg-card hover:bg-muted text-xs text-foreground disabled:opacity-50"
                      >
                        {restoringId === wg.id ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                        Ripristina
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateWorkgroupModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            navigate(`/${tenantSlug}/messenger/workgroups/${id}`);
          }}
        />
      )}
    </div>
  );
}
